import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fmtDate, timeAgo } from "./UI";
import ScanModelViewer from "./ScanModelViewer";
import ScanFilesPanel from "./ScanFilesPanel";
import "./scanViewer.css";
import { ensureCallableAuthContext } from "../lib/crmCallableAuth";
import {
  contactFilesFromCollection,
  contactScansFromCollection,
  fetchScanFileBlobUrl,
  isScanLikelyOrderReady,
  loadContactScans,
  loadScanViewerData,
  scanRowFromApi,
  scanRowFromDoc,
  scanRowFromFileDoc,
  scanStatusBadgeClass,
  scanStatusLabel,
  callableErrorMessage,
} from "../lib/scanViewerApi";

function ScanRowThumb({ scanId, fileId }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!scanId || !fileId) {
      setSrc("");
      return undefined;
    }
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchScanFileBlobUrl(scanId, fileId);
        if (!cancelled) setSrc(objectUrl);
      } catch (_) {
        if (!cancelled) setSrc("");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [scanId, fileId]);

  if (!src) return null;
  return <img src={src} alt="" className="scan-row-thumb" width={56} height={56} />;
}

function ScanViewerModal({ scanId, viewerData, loading, selectedMeshId, onSelectMesh, onClose }) {
  const meshFiles = viewerData?.mesh_files || [];
  const selectedOption = selectedMeshId ? meshFiles.find((mf) => mf.file_id === selectedMeshId) : null;
  const viewer = selectedOption?.viewer || viewerData?.viewer;
  const hasMesh = Boolean(viewer?.mesh_file_id);
  const shownOption = selectedOption || meshFiles.find((mf) => mf.viewer === viewer);
  const pickerNotes = [
    shownOption?.note || "",
    ...meshFiles
      .filter((mf) => !mf.viewer && mf.note)
      .map((mf) => `${mf.filename || mf.file_id}: ${mf.note}`),
  ].filter(Boolean);

  // A download streamed to disk by the files panel stops when the viewer
  // closes: ask first (a stray click on the backdrop would otherwise cancel it).
  const downloadsBusy = useRef(false);
  const setDownloadsBusy = useCallback((busy) => {
    downloadsBusy.current = busy;
  }, []);
  const requestClose = useCallback(() => {
    if (
      downloadsBusy.current &&
      !window.confirm("A download from this viewer is still running. Close the viewer and cancel it?")
    ) {
      return;
    }
    onClose();
  }, [onClose]);

  return (
    <div className="modal-overlay scan-viewer-modal-overlay" onClick={requestClose} role="presentation">
      <div
        className="modal scan-viewer-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Scan viewer"
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-3)" }}>Loading scan files…</div>
        ) : hasMesh ? (
          <>
            {meshFiles.length > 1 ? (
              <div className="scan-viewer-mesh-picker">
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>3D files</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {meshFiles.map((mf) => (
                    <button
                      key={mf.file_id}
                      type="button"
                      className={`btn btn-sm ${selectedMeshId === mf.file_id ? "btn-primary" : ""}`}
                      disabled={!mf.viewer}
                      title={mf.note || undefined}
                      onClick={() => onSelectMesh(mf.file_id)}
                    >
                      {mf.filename || mf.file_id}
                    </button>
                  ))}
                </div>
                {pickerNotes.map((note) => (
                  <div key={note} style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6, lineHeight: 1.5 }}>
                    {note}
                  </div>
                ))}
              </div>
            ) : null}
            <ScanModelViewer scanId={scanId} viewer={viewer} onClose={requestClose} />
          </>
        ) : (
          <div className="scan-viewer-empty">
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
              {viewerData?.scan?.scan_name || "Scan"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6, maxWidth: 480 }}>
              {viewerData?.viewer_message ||
                "No viewable mesh files found in the API or GCS for this scan."}
            </div>
            {viewerData?.chunk_count > 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 10 }}>
                {viewerData.chunk_count} capture chunk{viewerData.chunk_count === 1 ? "" : "s"} on server
                {viewerData.total_file_count ? ` · ${viewerData.total_file_count} total backend files` : ""}
              </div>
            ) : null}
            {viewerData?.files?.length ? (
              <div className="scan-viewer-files" style={{ marginTop: 16, borderTop: "none", paddingTop: 0 }}>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
                  Visible files ({viewerData.files.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {viewerData.files.map((f) => (
                    <span key={f.file_id} className="badge badge-muted" style={{ fontSize: 10 }}>
                      {f.filename || f.file_id}
                      {f.role ? ` · ${f.role}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            <div style={{ marginTop: 20 }}>
              <button type="button" className="btn btn-sm" onClick={requestClose}>
                Close
              </button>
            </div>
          </div>
        )}
        <ScanFilesPanel scanId={scanId} onBusyChange={setDownloadsBusy} />
      </div>
    </div>
  );
}

export default function ContactScansTab({ contact, scans = [], files = [] }) {
  const [apiRows, setApiRows] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");
  const [viewing, setViewing] = useState(null);
  const [viewerData, setViewerData] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [selectedMeshId, setSelectedMeshId] = useState("");
  const [err, setErr] = useState("");

  const fallbackRows = useMemo(() => {
    const scanRows = contactScansFromCollection(scans, contact).map(scanRowFromDoc);
    const fileRows = contactFilesFromCollection(files, contact).map(scanRowFromFileDoc);
    const seen = new Set(scanRows.map((r) => r.id));
    return [...scanRows, ...fileRows.filter((r) => !seen.has(r.id))];
  }, [scans, files, contact]);

  const rows = apiRows && apiRows.length > 0 ? apiRows : fallbackRows;

  useEffect(() => {
    const uid = contact?.firebaseUid;
    if (!uid) {
      setApiRows(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setListLoading(true);
      setListErr("");
      try {
        const auth = await ensureCallableAuthContext();
        if (!auth.ok) {
          if (!cancelled) setListErr(auth.message);
          return;
        }
        const scansFromApi = await loadContactScans(uid);
        if (!cancelled) setApiRows(scansFromApi.map(scanRowFromApi));
      } catch (e) {
        if (!cancelled) setListErr(callableErrorMessage(e));
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contact?.firebaseUid]);

  async function loadViewer(scanId, meshFileId = "") {
    setViewerLoading(true);
    setErr("");
    try {
      const data = await loadScanViewerData(scanId, meshFileId ? { meshFileId } : {});
      setViewerData(data);
      // The server ranks the packages (cloud model, then the on-device 3D model
      // when the app would show it, then the on-device preview); follow its pick.
      const primaryMesh = data?.viewer?.mesh_file_id || "";
      const firstMesh = data?.mesh_files?.find((mf) => mf.viewer)?.file_id || "";
      setSelectedMeshId(meshFileId || primaryMesh || firstMesh);
    } catch (e) {
      setErr(callableErrorMessage(e));
      setViewerData(null);
    } finally {
      setViewerLoading(false);
    }
  }

  async function openViewer(scanId) {
    setViewing(scanId);
    setViewerData(null);
    setSelectedMeshId("");
    const auth = await ensureCallableAuthContext();
    if (!auth.ok) {
      setErr(auth.message);
      return;
    }
    await loadViewer(scanId);
  }

  async function selectMesh(meshFileId) {
    if (!viewing || meshFileId === selectedMeshId) return;
    setSelectedMeshId(meshFileId);
    await loadViewer(viewing, meshFileId);
  }

  function closeViewer() {
    setViewing(null);
    setViewerData(null);
    setSelectedMeshId("");
    setErr("");
  }

  if (!contact?.firebaseUid && !contact?.id) {
    return (
      <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-3)" }}>
        Link a Firebase UID to inspect scans for this contact.
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 12, lineHeight: 1.5 }}>
        3D scans from the client portal, plus workspace photo uploads when no scan document exists. The 3D viewer
        streams files through the CRM GCS proxy; "Files &amp; downloads" in the viewer saves any file (15-minute links),
        the capture chunks or the keyframes.
      </div>

      {listLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 10 }}>Refreshing from backend API…</div>
      ) : null}

      {listErr ? (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            borderRadius: 8,
            background: "var(--amber-dim, rgba(234,179,8,0.12))",
            color: "var(--amber, #eab308)",
            fontSize: 13,
          }}
        >
          Could not refresh from API — showing Firestore list. {listErr}
        </div>
      ) : null}

      {err ? (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            borderRadius: 8,
            background: "var(--red-dim)",
            color: "var(--red)",
            fontSize: 13,
          }}
        >
          {err}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: "28px 0", color: "var(--text-3)" }}>
          No scans or uploads found for this contact yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((row) => {
            const orderReady = isScanLikelyOrderReady(row.status);
            const busy = viewing === row.id && viewerLoading;
            return (
              <div
                key={row.id}
                style={{
                  padding: 12,
                  borderRadius: 10,
                  border: "0.5px solid var(--border)",
                  background: "var(--bg-elevated)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  {row.thumbnailFileId ? (
                    <ScanRowThumb scanId={row.id} fileId={row.thumbnailFileId} />
                  ) : null}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{row.label}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                      <span className={`badge ${scanStatusBadgeClass(row.status)}`} style={{ fontSize: 10 }}>
                        {scanStatusLabel(row.status)}
                      </span>
                      {row.source === "firestore_file" ? (
                        <span className="badge badge-muted" style={{ fontSize: 10 }}>
                          {row.role === "photo" ? "Workspace photo" : "Workspace upload"}
                        </span>
                      ) : orderReady ? (
                        <span className="badge badge-green" style={{ fontSize: 10 }}>
                          Likely order-ready
                        </span>
                      ) : (
                        <span className="badge badge-amber" style={{ fontSize: 10 }}>
                          Review before ordering
                        </span>
                      )}
                      {row.meshFileCount > 0 ? (
                        <span className="badge badge-green" style={{ fontSize: 10 }}>
                          {row.meshFileCount} mesh file{row.meshFileCount === 1 ? "" : "s"}
                        </span>
                      ) : row.hasViewableMesh && row.chunkCount > 0 ? (
                        <span className="badge badge-green" style={{ fontSize: 10 }}>
                          Raw capture preview
                        </span>
                      ) : row.isV2 && row.chunkCount > 0 ? (
                        <span className="badge badge-muted" style={{ fontSize: 10 }}>
                          {row.chunkCount} chunk{row.chunkCount === 1 ? "" : "s"} uploaded
                        </span>
                      ) : row.isV2 ? (
                        <span className="badge badge-muted" style={{ fontSize: 10 }}>
                          Awaiting mesh
                        </span>
                      ) : null}
                      {row.sqftLabel !== "—" ? (
                        <span className="badge badge-muted" style={{ fontSize: 10 }}>
                          {row.sqftLabel}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", lineHeight: 1.5 }}>
                      {row.capturedAt ? (
                        <>
                          Captured {timeAgo(row.capturedAt)} · {fmtDate(new Date(row.capturedAt))}
                        </>
                      ) : (
                        "Capture date unknown"
                      )}
                      {row.source === "firestore_file" ? null : row.visibleFileCount > 0 ? (
                        <>
                          <br />
                          {row.visibleFileCount} visible file{row.visibleFileCount === 1 ? "" : "s"} on backend
                        </>
                      ) : null}
                      {row.workspaceName ? (
                        <>
                          <br />
                          {row.workspaceName}
                        </>
                      ) : row.workspaceId ? (
                        <>
                          <br />
                          Workspace <span className="mono">{row.workspaceId.slice(0, 12)}…</span>
                        </>
                      ) : null}
                      <br />
                      {row.source === "firestore_file" ? "File" : "Scan"}{" "}
                      <span className="mono">{row.id}</span>
                    </div>
                  </div>
                  {row.source === "firestore_file" ? null : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={busy}
                      onClick={() => openViewer(row.id)}
                    >
                      {busy ? "Loading…" : row.hasViewableMesh ? "View 3D" : "Open"}
                    </button>
                  </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewing ? (
        <ScanViewerModal
          scanId={viewing}
          viewerData={viewerData}
          loading={viewerLoading}
          selectedMeshId={selectedMeshId}
          onSelectMesh={selectMesh}
          onClose={closeViewer}
        />
      ) : null}
    </div>
  );
}
